import { Button, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import Modal, { CommonModalProps } from "..";
import { DbSubscriptionOfferInfos } from "../../../Core/Api/Types/db-subscription-offer-infos";
import { billingService } from "../../../Core/Api/services";
import SelectableCard from "../../Cards/SelectableCard";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export type ModalBillingChangeOfferElementProps = {
  offer: DbSubscriptionOfferInfos;
  priceModel: any;
  onSuccess: () => void;
  onClose: () => void;
};

export type ModalBillingChangeOfferProps = CommonModalProps<ModalBillingChangeOfferElementProps>;

const ModalBillingChangeOffer = React.forwardRef<any, ModalBillingChangeOfferProps>((props, ref) => {
  const { t } = useTranslation();
  const { onClose, onSuccess, closeModal, ...modalProps } = props;
  const { offer, priceModel } = modalProps;
  const [offers, setOffers] = React.useState<DbSubscriptionOfferInfos[]>([]);
  const [newOffer, setNewOffer] = React.useState<DbSubscriptionOfferInfos | undefined>(undefined);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleCancelClick = () => {
    closeModal();
    onClose && onClose();
  };

  useEffect(() => {
    billingService.ListSubscriptionOffers().then((response) => {
      setOffers(response.offers ?? []);
    });
  }, []);

  const onClickOffer = (offer: DbSubscriptionOfferInfos) => {
    setNewOffer(offer);
  };

  const onConfirm = useCallback(() => {
    if (!newOffer) return;

    billingService
      .UpdateMySubscription({
        newOfferId: newOffer.id,
        newLicences: null,
        simulate: false,
      })
      .then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.yourSubscriptionUpdated"), "success");
          closeModal();
          onSuccess && onSuccess();
        } else enqueueCustomSnackbar(t("alert:warning.changeYourPlan", { message: response.message }), "warning");
      });
  }, [newOffer, t]);

  return (
    <Modal
      title={t("modal:title.changeSubscriptionPlan")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={onConfirm} disabled={!newOffer}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {offers
          .filter((x) => x.id !== offer.id && x.modelData && x.modelData.global.cloud === priceModel.global.cloud)
          .map((offer) => (
            <SelectableCard key={offer.id} onClick={() => onClickOffer(offer)} selected={newOffer?.id === offer.id}>
              <Stack>
                <Typography variant="h5">{offer.name}</Typography>
                <Typography variant="body2" color="text.tier">
                  {t("modal:modalBillilngChangeOffer.body.monthlyCharge", { price: offer.modelData.global.basePrice })}
                </Typography>
              </Stack>
            </SelectableCard>
          ))}
      </Stack>
    </Modal>
  );
});

export default ModalBillingChangeOffer;
